<script>
    import Vue from 'vue'
    import appConfig from '@src/app.config'
    import Layout from '@layouts/main'
    import PageHeader from '@components/page-header'
    import * as axios from "axios";
    // import Swal from 'sweetalert2'
    import {
        required,
        minLength,
        sameAs
    } from 'vuelidate/lib/validators'

    const ACCOUNT_API_URL = process.env.VUE_APP_API_URL + 'dashboard/';

    export default {
        page: {
            title: 'Account',
            meta: [{ name: 'description', content: appConfig.description }],
        },
        components: { Layout, PageHeader },
        async mounted() {
            const response = await axios(ACCOUNT_API_URL + 'account');
            if (response.data.publisher) {
                this.publisher = response.data.publisher;
            }
            this.user = response.data
        },
        data() {
            return {
                title: 'My Account',
                items: [
                    {
                        text: 'Dashboard',
                        href: '/',
                    },
                    {
                        text: 'Account',
                        href: '/account',
                    },
                ],
                account: {},
                user: {
                    email: '',
                    name: '',
                    lastname: '',
                },
                publisher: {
                    name: '',
                    registeredCompanyName: '',
                    country: '',
                    city: '',
                    address: '',
                    phone: '',
                    bankAccount: '',
                },
                passwordForm: {
                    currentPassword: '',
                    newPassword: '',
                    confirmNewPassword: '',
                },
                passwordError: '',
                publisherSubmitted: false,
                passwordSubmitted: false,
                userSubmitted: true
            }
        },
        validations: {
            user: {
                name: { required }
            },
            publisher: {
                name: { required },
                country: { required },
                phone: { required },
            },
            passwordForm: {
                currentPassword: { required },
                newPassword: { required, minLength: minLength(6) },
                confirmNewPassword: { required, sameAsPassword: sameAs('newPassword') },
            },
        },
        methods: {
            async handleUserSubmit(e) {
                this.userSubmitted = true;
                this.$v.$touch();

                if(this.$v.user.pending || this.$v.user.$error) return;

                try {
                    await axios.put(`${ACCOUNT_API_URL }account/user`, this.user);
                    Vue.swal({
                        title: 'Good job!',
                        text: 'Your user info has been updated',
                        type: 'success',
                        confirmButtonClass: 'btn btn-confirm mt-2',
                    });

                }catch (e) {
                    Vue.swal({
                        type: 'error',
                        title: 'Oops...',
                        text: 'An error occurred',
                        confirmButtonClass: 'btn btn-confirm mt-2',
                    })
                    console.log(e);
                }
   


            },
            async handlePublisherSubmit(e) {
                this.publisherSubmitted = true;

                // stop here if form is invalid
                this.$v.$touch();

                if (this.$v.publisher.$pending || this.$v.publisher.$error) return;

                try {
                    await axios.put(`${ACCOUNT_API_URL }account/publisher`, this.publisher);
                    Vue.swal({
                        title: 'Good job!',
                        text: 'Your publisher info has been updated',
                        type: 'success',
                        confirmButtonClass: 'btn btn-confirm mt-2',
                    });
                } catch (e) {
                    Vue.swal({
                        type: 'error',
                        title: 'Oops...',
                        text: 'An error occurred',
                        confirmButtonClass: 'btn btn-confirm mt-2',
                    })
                    console.log(e);
                }
            },
            async handlePasswordChangeSubmit(e) {
                this.passwordSubmitted = true;
                // stop here if form is invalid
                this.$v.$touch();

                if (this.$v.passwordForm.$pending || this.$v.passwordForm.$error) return;

                try {
                    const response = await axios.put(`${ACCOUNT_API_URL }account/password`, this.passwordForm);
                    if (response.data.error) {
                        this.passwordError = response.data.error;
                        Vue.swal({
                            type: 'error',
                            title: 'Oops...',
                            text: response.data.error,
                            confirmButtonClass: 'btn btn-confirm mt-2',
                        })
                    } else {
                        this.passwordForm = {
                            currentPassword: '',
                            newPassword: '',
                            confirmNewPassword: '',
                        };
                        Vue.swal({
                            title: 'Good job!',
                            text: 'Your password has been changed',
                            type: 'success',
                            confirmButtonClass: 'btn btn-confirm mt-2',
                        });
                    }
                    this.passwordSubmitted = false

                } catch (e) {
                    console.log(e);
                }

            },
        },
    }
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <div class="row">
            <div class="col-lg-6">
                <div class="card-box">
                    <h4 class="header-title m-t-0 mb-2">User Info</h4>
                    <form @submit.prevent="handleUserSubmit">
                        <div class="form-group">
                            <label for="email">
                                Email
                            </label>
                            <input
                                    id="email"
                                    v-model="user.email"
                                    name="email"
                                    class="form-control disabled-text"
                                    type="text"
                                    disabled
                            />
                        </div>
                        <div class="form-group">
                            <label for="name">
                                Name
                                <span class="text-danger">*</span>
                            </label>
                            <input
                                    id="name"
                                    v-model="user.name"
                                    name="name"
                                    class="form-control"
                                    :class="{ 'is-invalid': userSubmitted && $v.user.name.$error}"
                                    type="text"
                                    placeholder="Enter name"
                            />                          
                        </div>
                        <div class="form-group">
                            <label>
                                Last Name
                            </label>
                            <input
                                    v-model="user.lastname"
                                    type="text"
                                    name="lastname"
                                    class="form-control"
                                    placeholder="Enter last name"
                            />
                        </div> 

                        <div class="form-group text-right m-b-0">
                            <button class="btn btn-primary" type="submit">Submit</button>
                        </div>
                    </form>
                </div>
                <!-- end card-box -->
                <div class="card-box">
                    <h4 class="header-title m-t-0 mb-2">Publisher Info</h4>
                    <form @submit.prevent="handlePublisherSubmit">
                        <div class="form-group">
                            <label for="publisherName">
                                Publisher Name
                            </label>
                            <input
                                    id="publisherName"
                                    v-model="publisher.name || ''"
                                    name="publisherName"
                                    class="form-control disabled-text"
                                    type="text"
                                    disabled
                            />
                        </div>
                        <div class="form-group">
                            <label for="name">
                                Registered Company Name
                                
                            </label>
                            <input
                                    id="lastName"
                                    v-model="publisher.registeredCompanyName"
                                    name="lastName"
                                    class="form-control"
                                    type="text"
                                    placeholder="Enter last name"
                            />                          
                        </div>
                        <div class="form-group">
                            <label>
                                Country
                                <span class="text-danger">*</span>
                            </label>
                            <input
                                    v-model="publisher.country"
                                    type="country"
                                    name="country"
                                    class="form-control"
                                    :class="{ 'is-invalid': publisherSubmitted && $v.publisher.country.$error }"
                                    placeholder="Enter country"
                            />
                            <div v-if="publisherSubmitted && $v.publisher.country.required" class="invalid-feedback">
                                This value is required.
                            </div>
                        </div>
                        <div class="form-group">
                            <label>
                                City
                                <!-- <span class="text-danger">*</span> -->
                            </label>
                            <input
                                    v-model="publisher.city"
                                    type="city"
                                    name="city"
                                    class="form-control"
                                    placeholder="Enter city"
                            />
                        </div>
                        <div class="form-group">
                            <label>
                                Address
                                <!-- <span class="text-danger">*</span> -->
                            </label>
                            <input
                                    v-model="publisher.address"
                                    type="address"
                                    name="address"
                                    class="form-control"
                                    placeholder="Enter address"
                            />
                        </div>
                        <div class="form-group">
                            <label>
                                Phone
                                <span class="text-danger">*</span>
                            </label>
                            <input
                                    v-model="publisher.phone"
                                    type="phone"
                                    name="phone"
                                    class="form-control"
                                    :class="{ 'is-invalid': publisherSubmitted && $v.publisher.phone.$error }"
                                    placeholder="Enter phone"
                            />
                            <div v-if="publisherSubmitted && $v.publisher.phone.required" class="invalid-feedback">
                                    This value is required.
                            </div>
                        </div>
                        <div class="form-group">
                            <label>
                                Payment Method
                            </label>
                            <input
                                    v-model="publisher.bankAccount"
                                    type="bankAccount"
                                    name="bankAccount"
                                    class="form-control"
                                    placeholder="Enter bankAccount"
                            />
                        </div>

                        <div class="form-group text-right m-b-0">
                            <button class="btn btn-primary" type="submit">Submit</button>
                        </div>
                    </form>
                </div>
            </div>

            <div class="col-lg-5">
                <div class="card-box">
                    <h4 class="header-title m-t-0 mb-2">Change Password</h4>

                    <form @submit.prevent="handlePasswordChangeSubmit">
                        <div class="form-group">
                            <label>
                                Password
                                <span class="text-danger">*</span>
                            </label>
                            <div>
                                <input
                                        v-model="passwordForm.currentPassword"
                                        type="password"
                                        name="password"
                                        class="form-control"
                                        :class="{ 'is-invalid': passwordSubmitted && $v.passwordForm.currentPassword.$error }"
                                        placeholder="Password"
                                />
                                <div v-if="passwordSubmitted && $v.passwordForm.currentPassword.equired" class="invalid-feedback">
                                    This value is required.
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>
                                New Password
                                <span class="text-danger">*</span>
                            </label>
                            <div>
                                <input
                                        v-model="passwordForm.newPassword"
                                        type="password"
                                        name="newPassword"
                                        class="form-control"
                                        :class="{ 'is-invalid': passwordSubmitted && $v.passwordForm.newPassword.$error }"
                                        placeholder="New Password"
                                />
                                <div
                                        v-if="passwordSubmitted && $v.passwordForm.newPassword.$error"
                                        class="invalid-feedback"
                                >
                                    <span v-if="!$v.passwordForm.newPassword.required">This value is required.</span>
                                    <span v-else-if="!$v.passwordForm.newPassword.sameAsPassword" >The password must be at least 6 characters long.</span>
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>
                                Confirm new Password
                                <span class="text-danger">*</span>
                            </label>
                            <div>
                                <input
                                        v-model="passwordForm.confirmNewPassword"
                                        type="password"
                                        name="confirmNewPassword"
                                        class="form-control"
                                        :class="{ 'is-invalid': passwordSubmitted && $v.passwordForm.confirmNewPassword.$error }"
                                        placeholder="Confirm new Password"
                                />
                                <div
                                        v-if="passwordSubmitted && $v.passwordForm.confirmNewPassword.$error"
                                        class="invalid-feedback"
                                >
                                    <span v-if="passwordSubmitted && !$v.passwordForm.confirmNewPassword.required">This value is required.</span>
                                    <span v-else-if="!$v.passwordForm.confirmNewPassword.sameAsPassword" >This value should be the same.</span>
                                </div>
                            </div>
                        </div>

                        <div class="form-group text-right">
                            <div>
                                <button type="submit" class="btn btn-primary">Change</button>
                                <div v-if="passwordError" class="invalid-feedback">
                                    <span>  {{passwordError}} </span>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
                <!-- end card-box -->
            </div>
            <!-- end col -->
            <!-- end col -->
        </div>

    </Layout>
</template>
<style>
.disabled-text.form-control:disabled{
        color: #999da0;
        background-color: #f2f2f2;
    }
</style>
